import React, { useRef, useState } from "react";
import * as PropTypes from "prop-types";
import { databasePropType } from "../../propTypes";
import { withStyles } from "@material-ui/core";
import { calculateChartHeight } from "./CountryExplorer";
import AbsoluteValuesSwitch from "../widgets/AbsoluteValuesSwitch";
import AreaChooser from "../choosers/AreaChooser";
import CountryExplorerChartTitle from "./CountryExplorerChartTitle";
import CountryExplorerCurrentChart from "./CountryExplorerCurrentChart";
import MethodChooser from "../choosers/MethodChooser";
import SectorChooser from "../choosers/SectorChooser";
import Switch from "../widgets/Switch";

const styles = {
  content: {
    margin: `4rem 0`
  },
  switchButton: {
    width: "6rem",
    fontSize: "1rem",
    padding: "4px 0"
  },
  toolbar: {
    width: "100%",
    margin: "2rem 0 2rem 1rem",
    paddingRight: "2rem",
    display: "flex",
    alignItems: "center"
  },
  toolbarItem: {
    margin: "0 15px",
    display: "inline-block"
  }
};

const getChangeDetails = (indicatorId, gniGroupId, methodId, sectorId) => ({
  widget: "countryExplorer.currentChart",
  payload: {
    indicator: indicatorId,
    gniGroup: gniGroupId,
    method: methodId,
    sector: sectorId
  }
});

const CountryExplorerCurrentChartWidget = props => {
  const [indicatorId, setIndicatorId] = useState(4);
  const [areaId, setAreaId] = useState(14);
  const [methodId, setMethodId] = useState();
  const [sectorId, setSectorId] = useState();
  const [absolute, setAbsolute] = useState(true);

  const ref = useRef();

  const chartType =
    methodId === -2 ? "method mix" : sectorId === -2 ? "sector mix" : "simple";

  return (
    <div>
      <CountryExplorerChartTitle
        database={props.database}
        indicatorId={indicatorId}
        areaId={areaId}
        methodId={methodId}
        sectorId={sectorId}
        fileStem="country_explorer_current_chart"
        chartRef={ref}
      >
        Explore the Current Situation
      </CountryExplorerChartTitle>

      <p>
        The graph below shows data by country for countries within the selected
        GNI group. Explore this data using the dropdowns and toggles below.
      </p>

      <div className={props.classes.toolbar}>
        <div className={props.classes.toolbarItem}>
          <Switch
            onText="Users"
            offText="Costs"
            checked={indicatorId === 4}
            buttonClassName={props.classes.switchButton}
            onToggle={() => {
              const id = indicatorId === 4 ? 6 : 4;

              setIndicatorId(id);

              props.onChange(getChangeDetails(id, areaId, methodId, sectorId));
            }}
          />
        </div>

        <div className={props.classes.toolbarItem}>
          <AreaChooser
            id="country-explorer-current-chart-area"
            label="GNI Group"
            areas={props.database.areas}
            onlyShowIncomeLevels={true}
            selectedKey={areaId}
            onChange={id => {
              setAreaId(id);

              props.onChange(
                getChangeDetails(indicatorId, id, methodId, sectorId)
              );
            }}
          />
        </div>

        <div className={props.classes.toolbarItem}>
          <MethodChooser
            id="country-explorer-current-chart-method"
            label="Method"
            methods={props.database.methods}
            selectedKey={methodId}
            allowMix={true}
            onChange={id => {
              setMethodId(id);

              const newSectorId =
                id === -2 && sectorId === -2 ? undefined : sectorId;

              if (!newSectorId) {
                setSectorId(undefined);
              }

              props.onChange(
                getChangeDetails(indicatorId, areaId, id, newSectorId)
              );
            }}
          />
        </div>

        <div className={props.classes.toolbarItem}>
          <SectorChooser
            id="country-explorer-current-chart-sector"
            label="Sector"
            sectorIds={new Set([-2, 6, 3, 5, 4])}
            selectedKey={sectorId}
            onChange={id => {
              setSectorId(id);

              const newMethodId =
                id === -2 && methodId === -2 ? undefined : methodId;

              if (!newMethodId) {
                setMethodId(undefined);
              }

              props.onChange(
                getChangeDetails(indicatorId, areaId, newMethodId, id)
              );
            }}
          />
        </div>

        <div className={props.classes.toolbarItem}>
          <AbsoluteValuesSwitch
            checked={absolute}
            buttonClassName={props.classes.switchButton}
            onToggle={() => {
              setAbsolute(!absolute);
            }}
          />
        </div>
      </div>

      <div className={props.classes.chart}>
        <CountryExplorerCurrentChart
          ref={ref}
          data={props.data}
          chartType={chartType}
          height={calculateChartHeight(
            1000,
            props.data.length /
              (chartType === "simple" ? 1 : chartType === "method mix" ? 7 : 3)
          )}
          indicator={indicatorId === 4 ? "Users" : "Costs"}
          method={
            methodId && methodId > 0
              ? props.database.methods[methodId]
              : undefined
          }
          sector={
            sectorId && sectorId > 0
              ? props.database.sectors[sectorId]
              : undefined
          }
          absolute={absolute}
        />
      </div>
    </div>
  );
};

CountryExplorerCurrentChartWidget.propTypes = {
  database: databasePropType,
  data: PropTypes.array,
  onChange: PropTypes.func.isRequired
};

export default withStyles(styles)(CountryExplorerCurrentChartWidget);
